import React from 'react'
import { ShieldAlert, Check, X, Calendar, User } from 'lucide-react'

interface AlertCenterProps {
  requests: any[]
  assignments: any[]
  subjects: any[]
  classrooms: any[]
  teachers: any[]
  onApprove: (id: number) => void
  onReject: (id: number) => void
}

export const AlertCenter: React.FC<AlertCenterProps> = ({
  requests,
  assignments,
  subjects,
  classrooms,
  teachers,
  onApprove,
  onReject
}) => {
  const pendingRequests = requests.filter(r => r.status === 'pending')

  const formatDate = (dateStr: string) => {
    try {
      const d = new Date(dateStr)
      return d.toLocaleDateString('th-TH', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch {
      return dateStr
    }
  }

  if (pendingRequests.length === 0) {
    return null // Return nothing if there are no pending requests to keep UI clean!
  }

  return (
    <div style={{
      background: 'rgba(239, 68, 68, 0.03)',
      border: '1px solid rgba(239, 68, 68, 0.15)',
      borderRadius: '16px',
      padding: '24px',
      marginBottom: '24px',
      boxShadow: '0 4px 20px rgba(239, 68, 68, 0.01)'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
        <ShieldAlert size={20} color="#ef4444" style={{ animation: 'pulse 2s infinite' }} />
        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#ef4444' }}>
          แจ้งเตือนคำร้องขอแก้ไขคะแนน ({pendingRequests.length})
        </h3>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {pendingRequests.map((req) => {
          const assign = assignments.find(a => a.id === req.teaching_assignment_id)
          const subject = assign ? subjects.find(s => s.id === assign.subject_id) : null
          const classroom = assign ? classrooms.find(c => c.id === assign.classroom_id) : null
          const teacher = teachers.find(p => p.id === req.requested_by || p.id === req.teacher_id)
          
          const subjectStr = subject ? `${subject.code} - ${subject.name}` : 'ไม่ทราบวิชา'
          const classStr = classroom ? `ม.${classroom.grade_level >= 7 ? classroom.grade_level - 6 : classroom.grade_level}/${classroom.room_number}` : 'ไม่ทราบห้อง'
          const teacherName = teacher ? `${teacher.first_name} ${teacher.last_name}` : 'ครูผู้สอน'

          return (
            <div key={req.id} style={{
              background: 'var(--bg-card, #ffffff)',
              border: '1px solid var(--border-color, rgba(0, 0, 0, 0.08))',
              borderRadius: '12px',
              padding: '16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '16px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.01)'
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1, minWidth: '280px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <span style={{
                    fontSize: '12px',
                    fontWeight: 600,
                    padding: '2px 8px',
                    borderRadius: '20px',
                    background: req.test_type === 'pre' ? 'rgba(59, 130, 246, 0.1)' : 'rgba(16, 185, 129, 0.1)',
                    color: req.test_type === 'pre' ? '#3b82f6' : '#10b981'
                  }}>
                    {req.test_type === 'pre' ? 'Pre-Test' : 'Post-Test'}
                  </span>
                  <strong style={{ fontSize: '14px' }}>{classStr} &bull; {subjectStr}</strong>
                </div>

                <div style={{ display: 'flex', gap: '16px', fontSize: '13px', color: 'var(--text-secondary, #64748b)' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <User size={14} />
                    {teacherName}
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Calendar size={14} />
                    {formatDate(req.created_at)}
                  </span>
                </div>

                <div style={{
                  fontSize: '13px',
                  background: 'var(--bg-muted, #f8fafc)',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  borderLeft: '3px solid #ef4444',
                  margin: '4px 0 0 0',
                  color: 'var(--text-primary, #1e293b)'
                }}>
                  เหตุผล: "{req.reason}"
                </div>
              </div>

              {/* Action buttons */}
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  type="button"
                  onClick={() => onApprove(req.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '8px 16px',
                    borderRadius: '8px',
                    background: '#10b981',
                    color: '#ffffff',
                    border: 'none',
                    fontWeight: 600,
                    fontSize: '13px',
                    cursor: 'pointer',
                    boxShadow: '0 2px 4px rgba(16,185,129,0.1)',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => (e.currentTarget.style.background = '#059669')}
                  onMouseOut={(e) => (e.currentTarget.style.background = '#10b981')}
                >
                  <Check size={14} />
                  อนุมัติ
                </button>
                <button
                  type="button"
                  onClick={() => onReject(req.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '8px 16px',
                    borderRadius: '8px',
                    background: 'transparent',
                    color: '#ef4444',
                    border: '1px solid rgba(239, 68, 68, 0.2)',
                    fontWeight: 600,
                    fontSize: '13px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = 'rgba(239,68,68,0.05)'
                    e.currentTarget.style.borderColor = '#ef4444'
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = 'transparent'
                    e.currentTarget.style.borderColor = 'rgba(239,68,68,0.2)'
                  }}
                >
                  <X size={14} />
                  ปฏิเสธ
                </button>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
